import * as React from 'react';

/**
 * Reference-counted scroll lock.
 *
 * Multiple overlays (Modal, AlertDialog, Drawer) can call `useScrollLock(true)`
 * concurrently. The lock is applied when the first consumer activates and
 * released only when the last consumer deactivates. This prevents the bug where
 * closing an inner overlay restores scrolling while an outer overlay is still open.
 *
 * Layout-shift prevention:
 * When `overflow: hidden` is set, the scrollbar disappears and content would
 * jump left by the scrollbar width. We compensate by adding equivalent
 * padding-right to `<body>`, keeping the content in the same position.
 */

let lockCount = 0;
let savedOverflow = '';
let savedPaddingRight = '';

function lock(): void {
  lockCount += 1;
  if (lockCount > 1) return;

  if (typeof document === 'undefined') return;

  const html = document.documentElement;
  const body = document.body;

  savedOverflow = body.style.overflow;
  savedPaddingRight = body.style.paddingRight;

  const scrollbarWidth = window.innerWidth - html.clientWidth;
  body.style.overflow = 'hidden';
  if (scrollbarWidth > 0) {
    body.style.paddingRight = `${scrollbarWidth}px`;
  }
}

function unlock(): void {
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount > 0) return;

  if (typeof document === 'undefined') return;

  const body = document.body;
  body.style.overflow = savedOverflow;
  body.style.paddingRight = savedPaddingRight;
}

/**
 * Force-release all scroll locks. Call before hard navigations
 * (`window.location.href`, `window.location.reload`) that bypass
 * React's cleanup cycle and would otherwise leave the body locked.
 */
export function resetScrollLock(): void {
  if (typeof document === 'undefined') return;
  lockCount = 0;
  document.body.style.overflow = savedOverflow;
  document.body.style.paddingRight = savedPaddingRight;
}

export function useScrollLock(active: boolean): void {
  React.useEffect(() => {
    if (!active) return undefined;

    lock();
    return () => unlock();
  }, [active]);
}
